export enum OnboardingStep {
  PERSONAL_CONNECTION = 1,
  TEST_OR_REAL_DETERMINATION = 2,
  CUSTOMER_AUDIENCE_UNDERSTANDING = 3,
  BLOCKERS_IDENTIFICATION = 4,
  VISITOR_QUESTIONS_IDENTIFICATION = 5,
  ACTIVE_LISTENING = 6,
  CAPABILITY_DEMONSTRATION = 7,
  FEEDBACK_REQUEST = 8,
  CALLS_TO_ACTION = 9,
  MISCONCEPTIONS_IDENTIFICATION = 10,
  NEXT_STEP_GAUGING = 11,
  INSTALLATION_CONFIRMATION = 12,
  WRAP_UP = 13,
}

export interface StoreSettingsResponseDto {
  id: string;

  storeId: string;

  defaultPlaceholderText: string;

  settings?: Record<string, unknown>;

  implementationCode: string;

  createdAt: string;

  updatedAt: string;
}

export interface Subscription {
  name: string;
  currentStatus: string;
}

export interface Store {
  id: string;
  name: string;
  url: string;
  config: {
    config: StoreConfig;
  };
  subscription?: Subscription;
}

export enum GalleryOrientation {
  HORIZONTAL = 'horizontal',
  VERTICAL = 'vertical',
}

export interface StoreConfig {
  type: string;
  answerConfig: {
    enableOnPatterns: string[];
    excludeOnPatterns: string[];
    productIdentifierMatch: string;
  };
  platformType: 'aero' | 'woocommerce' | 'magento' | 'shopify' | 'website';
  searchConfig?: {
    inputSelector: string;
    formSelector: string;
    headerSelector?: string;
    galleryTopMargin?: number;
    superbarRootParent?: string;
  };
  placeholdersConfig: {
    homePlaceholders: string[];
  };
  featureFlags?: {
    visibilityFlag: {
      flagKey: string;
      visibleOnVariant: string;
    };
  };
  moduleEnabled: string[];
  localization: {
    locale: string;
    currency: string;
    decimalPlaces: number;
    brandColor: string;
  };
  galleryOrientation: GalleryOrientation;
  htmlSelectors?: {
    onTopOfSuperbar?: string;
  };
  goLiveEnabled?: boolean;
  hidePoweredBySuperbar?: boolean;
  amplitudeEnabled?: boolean;
  proactiveEngagementTimeoutSeconds?: number;
  engagementConfig?: {
    exitIntentEnabled?: boolean;
    exitIntentQuestion?: string;
    autoExpandDelaySeconds?: number;
    delayedLoadSeconds?: number;
    expandOnFooterEnabled?: boolean;
    hideOnScrollEnabled?: boolean;
    conversionUrls?: string[];
    purchaseAutoDetectEnabled?: boolean;
  };
}

export enum ResponseType {
  ASSISTANT = 'assistant',
  PRODUCT_SEARCH = 'product-search',
  CATEGORY_ROUTE = 'category-route',
}

export interface Session {
  id: string;
  status: string;
  store: Store;
  createdAt: Date;
  updatedAt: Date;
  token: string;
}

export interface Message {
  type: 'bot' | 'user';
  message: string;
  productReferences?: {
    product: ProductDisplayFields;
    id: string;
  }[];
}

export enum DisplayFilterType {
  FREEFORM = 'freeform',
  FILTER = 'filter',
}

export interface DisplayFilter {
  filterName: string;
  label: string;
  value: string;
  type?: DisplayFilterType;
}

export interface DisplayFilterGroup {
  label: string;
  value: string;
  type?: DisplayFilterType;
  filters: DisplayFilter[];
}

export interface DefaultAction {
  type: DefaultActionType;
  actionProps: {
    href: string;
  };
}

export type DefaultActionType = 'link' | 'navigate';

export interface SuggestedCTA {
  name: string;
  description: string;
  type: string;
  key: string;
  defaultAction?: DefaultAction;
  designation: 'primary' | 'secondary' | 'none';
}

export interface Citation {
  index: number;
  title: string;
  url: string | null;
  snippet: string;
}

export interface UnifiedSearchApiResponse {
  id?: string;
  parentSearchId?: string;
  answer: string;
  filtersAvailable?: DisplayFilter[];
  appliedFilters?: DisplayFilter[];
  filteredDocuments: ProductDisplayFields[];
  responseType: ResponseType;
  currentPage: number;
  totalPages: number;
  count: number;
  query: string;
  suggestedCTA?: SuggestedCTA;
  suggestedURL?: string;
  redirectUrl?: string;
  onboardingStep?: OnboardingStep;
  autoHideTimeout?: number;
  citations?: Citation[];
}

export interface DynamicHintsResponse {
  hints: string[];
}

export interface ProductDisplayFields {
  name: string;
  description: string;
  regularPrice: string;
  salePrice: string;
  images: string[];
  parentName: string;
  slug: string;
  manufacturer: string;
}

// Reuse the actual DTO for better maintainability and single source of truth
export type StoreSettings = StoreSettingsResponseDto;

/**
 * State machine for onboarding initialization lifecycle
 * Tracks the progress of fetching and displaying the initial onboarding message
 */
export enum OnboardingInitializationState {
  /** Not started yet, waiting for session and store */
  IDLE = 'idle',
  /** Currently fetching onboarding state from API */
  PENDING = 'pending',
  /** Initialization completed, won't run again */
  DONE = 'done',
}
